import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTelegramPlane,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaWhatsapp,
  FaReddit,
  FaPinterest
} from "react-icons/fa";
import apiClient from "../../Services/apiInterceptor";
import { socialPostService } from "../../Services/socialPostService";
import { path_url } from "../../Config/config";
import MainSidebar from "../Mainsidebar";
import UserNavbar from "../UserNavbar";

/* ─── helpers ────────────────────────────────────────────── */
const getUserId = () => localStorage.getItem("userId") || "";

const getMediaAbsoluteUrl = (url) => {
  if (!url) return "";
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:")
  ) {
    return url;
  }
  return `${path_url}/ticket/file/00000000-0000-0000-0000-000000000000/${url}`;
};

const getConfigObj = (data) => {
  if (!data) return null;
  let d = data;
  if (data.statusCode && Array.isArray(data.body)) d = data.body;
  if (data.statusCode === "OK" && (!data.body || data.body.length === 0))
    return null;
  if (Array.isArray(d)) return d.length > 0 ? d[0] : null;
  if (typeof d === "object" && Object.keys(d).length === 0) return null;
  return d;
};

const C = {
  blue: "#3B82F6",
  blueDeep: "#2563eb",
  blueSoft: "#EAF6FF",
  blueMid: "#D6EEFF",
  blueLight: "#A7D8FF",
  white: "#ffffff",
  text: "#1e3a5f",
  textSub: "#4b6e91",
  textMuted: "#93b5d0",
  border: "#D6EEFF",
  error: "#dc2626",
  success: "#16a34a",
  warning: "#d97706",
  grayBg: "#f8fafc",
};

const PLATFORMS = {
  telegram: { label: "Telegram", icon: <FaTelegramPlane />, color: "#26a5e4" },
  facebook: { label: "Facebook", icon: <FaFacebookF />, color: "#1877f2" },
  instagram: { label: "Instagram", icon: <FaInstagram />, color: "#e4405f" },
  linkedin: { label: "LinkedIn", icon: <FaLinkedinIn />, color: "#0a66c2" },
};

const s = {
  inner: {
    maxWidth: "1200px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  headerSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "16px",
    borderBottom: `1px solid ${C.border}`,
    paddingBottom: "20px",
  },
  headerTitle: {
    fontSize: "1.8rem",
    fontWeight: 800,
    color: C.text,
    margin: 0,
  },
  headerSubtitle: {
    color: C.textSub,
    fontSize: "0.92rem",
    margin: "6px 0 0 0",
  },
  headerActions: { display: "flex", gap: "10px" },
  actionBtn: {
    background: C.white,
    border: `1.5px solid ${C.blueLight}`,
    color: C.blue,
    borderRadius: "10px",
    padding: "8px 18px",
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.18s",
  },

  card: {
    background: C.white,
    borderRadius: "12px",
    boxShadow: "0 4px 18px rgba(59,130,246,0.06)",
    border: `1px solid ${C.border}`,
    overflow: "hidden",
  },
  cardHead: {
    borderBottom: `1px solid ${C.border}`,
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: C.blueSoft,
  },
  cardHeadTitle: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: C.text,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  cardBody: { padding: "20px" },

  // Filter bar
  filterBar: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "16px",
    marginBottom: "20px",
    background: C.grayBg,
    padding: "16px",
    borderRadius: "8px",
    border: `1px solid ${C.border}`,
  },
  filterField: { display: "flex", flexDirection: "column", gap: "6px" },
  filterLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: C.textSub,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    border: `1.5px solid ${C.border}`,
    borderRadius: "6px",
    fontSize: "0.88rem",
    color: C.text,
    outline: "none",
    background: C.white,
  },
  select: {
    width: "100%",
    padding: "8px 12px",
    border: `1.5px solid ${C.border}`,
    borderRadius: "6px",
    fontSize: "0.88rem",
    color: C.text,
    outline: "none",
    background: C.white,
    cursor: "pointer",
  },

  // Table Styling
  tableWrap: { overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
    fontSize: "0.9rem",
  },
  th: {
    padding: "12px 16px",
    background: C.grayBg,
    color: C.textSub,
    fontWeight: 700,
    borderBottom: `2px solid ${C.border}`,
    fontSize: "0.82rem",
    textTransform: "uppercase",
  },
  td: {
    padding: "14px 16px",
    borderBottom: `1px solid ${C.border}`,
    color: C.text,
    verticalAlign: "middle",
  },
  trHover: {
    transition: "background 0.15s",
    ":hover": { background: C.blueSoft },
  },

  // Badges & Labels
  platformBadge: (color) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: `${color}15`,
    color: color,
    padding: "4px 10px",
    borderRadius: "12px",
    fontWeight: 600,
    fontSize: "0.8rem",
  }),
  typeBadge: (type) => ({
    display: "inline-block",
    background: "#f1f5f9",
    color: C.textSub,
    padding: "2px 8px",
    borderRadius: "4px",
    fontWeight: 600,
    fontSize: "0.75rem",
  }),
  statusBadge: {
    display: "inline-block",
    background: "#e0f2fe",
    color: C.blue,
    padding: "2px 8px",
    borderRadius: "12px",
    fontWeight: 600,
    fontSize: "0.75rem",
    border: `1px solid ${C.blueLight}`,
  },

  // Action Buttons
  btnView: {
    background: C.blueSoft,
    color: C.blue,
    border: `1px solid ${C.blueLight}`,
    borderRadius: "6px",
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.8rem",
    marginRight: "8px",
    transition: "all 0.15s",
  },
  btnDelete: {
    background: "#fef2f2",
    color: C.error,
    border: "1px solid #fecaca",
    borderRadius: "6px",
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.8rem",
    transition: "all 0.15s",
  },

  // Modals
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(30,58,95,0.4)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    padding: "20px",
  },
  modalCard: {
    background: C.white,
    borderRadius: "12px",
    width: "100%",
    maxWidth: "600px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
    overflow: "hidden",
    border: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column",
  },
  modalHead: {
    background: C.blueSoft,
    borderBottom: `1px solid ${C.border}`,
    padding: "16px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: { fontSize: "1.1rem", fontWeight: 800, color: C.text, margin: 0 },
  modalClose: {
    background: "none",
    border: "none",
    fontSize: "1.2rem",
    cursor: "pointer",
    color: C.textSub,
  },
  modalBody: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxHeight: "75vh",
    overflowY: "auto",
  },
  modalFooter: {
    borderTop: `1px solid ${C.border}`,
    padding: "16px 24px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    background: C.grayBg,
  },

  detailRow: {
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    borderBottom: `1px solid ${C.grayBg}`,
    paddingBottom: "12px",
  },
  detailLabel: { fontWeight: 700, color: C.textSub, fontSize: "0.85rem" },
  detailValue: { color: C.text, fontSize: "0.9rem", wordBreak: "break-all" },

  // Media Preview in modal
  mediaPreview: {
    background: C.blueSoft,
    borderRadius: "8px",
    padding: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    maxHeight: "250px",
    overflow: "hidden",
    border: `1px solid ${C.border}`,
    marginTop: "8px",
  },

  toast: (type) => ({
    position: "fixed",
    bottom: "28px",
    right: "28px",
    zIndex: 9999,
    background:
      type === "success" ? C.success : type === "error" ? C.error : C.blue,
    color: "#fff",
    borderRadius: "10px",
    padding: "12px 20px",
    fontWeight: 600,
    fontSize: "0.9rem",
    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    maxWidth: "350px",
  }),
  emptyState: {
    padding: "40px 20px",
    textAlign: "center",
    color: C.textSub,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  },

  // Pagination
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    flexWrap: "wrap",
    gap: "12px",
  },
  pagBtn: (disabled) => ({
    background: disabled ? "#f1f5f9" : C.white,
    border: `1.5px solid ${C.border}`,
    color: disabled ? C.textMuted : C.blue,
    padding: "6px 14px",
    borderRadius: "6px",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.15s",
  }),
  pagInfo: { fontSize: "0.85rem", color: C.textSub },
};

export default function EnteliSocialPosts() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const isMounted = useRef(true);

  // Filters State
  const [platformFilter, setPlatformFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // View Details Modal State
  const [activePost, setActivePost] = useState(null);
  const [fetchingDetail, setFetchingDetail] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  // Modal Interactivity States
  const [isZoomed, setIsZoomed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);

  // Delete Modal State
  const [postToDelete, setPostToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Helper download method
  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || "attachment";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      window.open(url, "_blank");
    }
  };

  // Layout wrapper states
  const [hamburger, sethamburger] = useState(false);
  const [isRightMenu, setisRightMenu] = useState(false);
  const [isSearchShow, setIsSearchShow] = useState(false);
  const [fullScreen, setFullScreen] = useState(true);

  // Layout methods
  const handleScreenEnter = () => {
    setFullScreen(false);
    document.body.requestFullscreen();
  };
  const handleScreenExit = () => {
    setFullScreen(true);
    document.exitFullscreen();
  };
  const handelShow = () => setIsSearchShow(!isSearchShow);
  const handelRightMenuShow = () => setisRightMenu(!isRightMenu);

  const handleclickonbutton = (hamburger) => {
    if (document.documentElement.clientWidth <= 800)
      return { width: "100%", left: "0rem" };
    else
      return {
        width: !hamburger && "-webkit-fill-available",
        left: !hamburger && "3.7rem",
      };
  };

  const handleclickonbutton1 = (hamburger) => {
    if (document.documentElement.clientWidth <= 800) {
      return { display: hamburger && "none" };
    }
  };

  const showToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => {
      if (isMounted.current) setToast(null);
    }, 3500);
  };

  const fetchPostsFromBackend = async () => {
    setLoading(true);
    try {
      const usercid = getUserId();
      if (!usercid) return;

      // 1. Fetch Facebook Config to get facebookId
      let facebookId = null;
      try {
        const fbRes = await apiClient.get(`/facebook/userid/${usercid}`);
        const fbConfig = getConfigObj(fbRes?.data);
        facebookId = fbConfig?.id;
      } catch (err) { }

      const promises = [];

      // Determine platform queries
      const platformsToQuery = [];
      if (platformFilter === "all") {
        platformsToQuery.push("telegram");
        if (facebookId) platformsToQuery.push("facebook");
      } else if (platformFilter === "telegram") {
        platformsToQuery.push("telegram");
      } else if (platformFilter === "facebook") {
        if (facebookId) {
          platformsToQuery.push("facebook");
        } else {
          showToast(
            "Facebook configuration is missing. Configure it in Settings.",
            "warning",
          );
        }
      }

      platformsToQuery.forEach((plat) => {
        const configId = plat === "telegram" ? usercid : facebookId;

        let promise;
        if (typeFilter === "all") {
          promise = socialPostService.getPosts(plat, configId);
        } else {
          promise = socialPostService.getPostsByType(
            plat,
            configId,
            typeFilter,
          );
        }

        promises.push(
          promise
            .then((data) => {
              const list = Array.isArray(data) ? data : data?.body || [];
              return list.map((item) => ({
                ...item,
                platform: plat,
                id: item.id || item.postId,
                message:
                  item.message ||
                  item.caption ||
                  item.text ||
                  item.description ||
                  item.content ||
                  "",
                postType:
                  item.postType ||
                  item.mediaType ||
                  (item.mediaUrl ||
                    item.attachmentUrl ||
                    item.imageUrl ||
                    item.photoUrl ||
                    item.videoUrl ||
                    item.fileUrl
                    ? "IMAGE"
                    : "TEXT"),
                mediaUrl: getMediaAbsoluteUrl(
                  item.mediaUrl ||
                  item.attachmentUrl ||
                  item.imageUrl ||
                  item.photoUrl ||
                  item.videoUrl ||
                  item.fileUrl ||
                  "",
                ),
                publishedDate:
                  item.publishedDate || item.createdAt || item.date || "",
              }));
            })
            .catch((err) => {
              console.error(`Error loading posts for ${plat}:`, err);
              return [];
            }),
        );
      });

      const results = await Promise.all(promises);
      const combined = results.flat();

      // Sort by date descending
      combined.sort(
        (a, b) =>
          new Date(b.publishedDate || 0) - new Date(a.publishedDate || 0),
      );

      if (isMounted.current) {
        setPosts(combined);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error("Error loading posts:", err);
      showToast("Error loading posts from backend.", "error");
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;
    fetchPostsFromBackend();
    return () => {
      isMounted.current = false;
    };
  }, [platformFilter, typeFilter]);

  // Handle local searching and date filters
  useEffect(() => {
    let result = [...posts];

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.message?.toLowerCase().includes(q) ||
          p.id?.toString().toLowerCase().includes(q),
      );
    }

    // Date range filter
    if (fromDate) {
      const from = new Date(fromDate);
      result = result.filter((p) => new Date(p.publishedDate) >= from);
    }
    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999); // inclusive of to date
      result = result.filter((p) => new Date(p.publishedDate) <= to);
    }

    setFilteredPosts(result);
    setCurrentPage(1);
  }, [posts, searchQuery, fromDate, toDate]);

  // View Modal Flow
  const handleOpenViewModal = async (platform, id) => {
    setFetchingDetail(true);
    setShowViewModal(true);
    // Reset modal interactivity states
    setIsZoomed(false);
    setCopied(false);
    setImgError(false);
    setMediaLoading(true);
    try {
      const detail = await socialPostService.getPost(platform, id);
      if (isMounted.current) {
        const parsedMediaUrl = getMediaAbsoluteUrl(
          detail?.mediaUrl ||
          detail?.attachmentUrl ||
          detail?.imageUrl ||
          detail?.photoUrl ||
          detail?.videoUrl ||
          detail?.fileUrl ||
          "",
        );
        setActivePost({
          platform,
          id: detail?.id || id,
          message:
            detail?.message ||
            detail?.caption ||
            detail?.text ||
            detail?.description ||
            detail?.content ||
            "",
          postType:
            detail?.postType ||
            detail?.mediaType ||
            (parsedMediaUrl ? "IMAGE" : "TEXT"),
          mediaUrl: parsedMediaUrl,
          publishedDate:
            detail?.publishedDate || detail?.createdAt || detail?.date || "",
        });
        if (!parsedMediaUrl) {
          setMediaLoading(false);
        }
      }
    } catch (err) {
      console.error("Error fetching post detail:", err);
      showToast("Error fetching complete post details.", "error");
      setShowViewModal(false);
    } finally {
      if (isMounted.current) {
        setFetchingDetail(false);
      }
    }
  };

  // Delete Modal Flow
  const handleOpenDeleteModal = (post) => {
    setPostToDelete(post);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!postToDelete) return;
    setDeleting(true);
    try {
      await socialPostService.deletePost(
        postToDelete.platform,
        postToDelete.id,
      );
      showToast("Post deleted successfully from backend history.", "success");
      setShowDeleteModal(false);
      setPostToDelete(null);
      // Reload the data
      fetchPostsFromBackend();
    } catch (err) {
      console.error("Error deleting post:", err);
      showToast("Delete failed. Please try again later.", "error");
    } finally {
      if (isMounted.current) {
        setDeleting(false);
      }
    }
  };

  // Pagination calculation
  const indexOfLastPost = currentPage * itemsPerPage;
  const indexOfFirstPost = indexOfLastPost - itemsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / itemsPerPage);

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css?family=Nunito:200&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css?family=Muli:200&display=swap"
        rel="stylesheet"
      />
      <div className="con1 d-flex">
        <div className="subcon1" style={handleclickonbutton1(hamburger)}>
          <MainSidebar hamburger={hamburger} sethamburger={sethamburger} />
        </div>
        <div className="subcon2" style={handleclickonbutton(hamburger)}>
          <UserNavbar
            hamburger={hamburger}
            sethamburger={sethamburger}
            handelRightMenuShow={handelRightMenuShow}
            handelShow={handelShow}
            isSearchShow={isSearchShow}
            handleScreenEnter={handleScreenEnter}
            handleScreenExit={handleScreenExit}
            fullScreen={fullScreen}
          />
          <div
            className="content-wrapper"
            style={{
              padding: "24px",
              background: "#fafbfe",
              minHeight: "calc(100vh - 56px)",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            <div style={s.inner}>
              {/* Header */}
              <div style={s.headerSection}>
                <div>
                  <h1 style={s.headerTitle}>📋 Enteli Social Posts History</h1>
                  <p style={s.headerSubtitle}>
                    View and manage all social content published from this
                    account
                  </p>
                </div>
                <div style={s.headerActions}>
                  <button
                    style={s.actionBtn}
                    onClick={() =>
                      navigate(
                        "/crm-dashboard/enteli-social-redesign-dashboard",
                      )
                    }
                  >
                    📡 Dashboard
                  </button>
                  <button
                    style={s.actionBtn}
                    onClick={() =>
                      navigate("/crm-dashboard/enteli-social-redesign-settings")
                    }
                  >
                    ⚙️ Settings
                  </button>
                </div>
              </div>

              {/* Main Card */}
              <div style={s.card}>
                <div style={s.cardHead}>
                  <span style={s.cardHeadTitle}>Published Posts</span>
                </div>

                <div style={s.cardBody}>
                  {/* Filters Block */}
                  <div style={s.filterBar}>
                    <div style={s.filterField}>
                      <span style={s.filterLabel}>Search</span>
                      <input
                        style={s.input}
                        type="text"
                        placeholder="Search by ID or message..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>

                    <div style={s.filterField}>
                      <span style={s.filterLabel}>Platform</span>
                      <select
                        style={s.select}
                        value={platformFilter}
                        onChange={(e) => setPlatformFilter(e.target.value)}
                      >
                        <option value="all">All Platforms</option>
                        <option value="telegram">✈️ Telegram</option>
                        <option value="facebook">📘 Facebook</option>
                        <option value="instagram" disabled>
                          📸 Instagram (Coming Soon)
                        </option>
                        <option value="linkedin" disabled>
                          💼 LinkedIn (Coming Soon)
                        </option>
                      </select>
                    </div>

                    <div style={s.filterField}>
                      <span style={s.filterLabel}>Post Type</span>
                      <select
                        style={s.select}
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                      >
                        <option value="all">All Types</option>
                        <option value="text">Text</option>
                        <option value="image">Image</option>
                        <option value="video">Video</option>
                        <option value="link">Link</option>
                      </select>
                    </div>

                    <div style={s.filterField}>
                      <span style={s.filterLabel}>From Date</span>
                      <input
                        style={s.input}
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                    </div>

                    <div style={s.filterField}>
                      <span style={s.filterLabel}>To Date</span>
                      <input
                        style={s.input}
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Loading State */}
                  {loading ? (
                    <div style={s.emptyState}>
                      <span
                        style={{
                          fontSize: "2rem",
                          animation: "spin 1s linear infinite",
                          display: "inline-block",
                        }}
                      >
                        ⏳
                      </span>
                      <div>Loading published posts from APIs...</div>
                    </div>
                  ) : filteredPosts.length === 0 ? (
                    /* Empty state */
                    <div style={s.emptyState}>
                      <span style={{ fontSize: "2.5rem" }}>📭</span>
                      <div>No published posts match your filters.</div>
                      <button
                        style={{ ...s.actionBtn, marginTop: "10px" }}
                        onClick={() => {
                          setSearchQuery("");
                          setPlatformFilter("all");
                          setTypeFilter("all");
                          setFromDate("");
                          setToDate("");
                        }}
                      >
                        Reset All Filters
                      </button>
                    </div>
                  ) : (
                    /* Table Container */
                    <div style={s.tableWrap}>
                      <table style={s.table}>
                        <thead>
                          <tr>
                            <th style={s.th}>Post ID</th>
                            <th style={s.th}>Platform</th>
                            <th style={s.th}>Type</th>
                            <th style={s.th}>Message</th>
                            <th style={s.th}>Published Date</th>
                            <th style={s.th}>Status</th>
                            <th style={s.th}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentPosts.map((post, idx) => {
                            const pfMeta = PLATFORMS[post.platform] || {
                              label: post.platform,
                              icon: "📲",
                              color: "#64748b",
                            };
                            return (
                              <tr key={`${post.id}-${idx}`} style={s.trHover}>
                                <td style={s.td}>
                                  <span
                                    style={{
                                      fontFamily: "monospace",
                                      fontWeight: 600,
                                      fontSize: "0.8rem",
                                      color: C.textSub,
                                    }}
                                  >
                                    {post.id
                                      ? post.id.toString().substring(0, 15) +
                                      (post.id.toString().length > 15
                                        ? "..."
                                        : "")
                                      : "N/A"}
                                  </span>
                                </td>
                                <td style={s.td}>
                                  <span style={s.platformBadge(pfMeta.color)}>
                                    <span>{pfMeta.icon}</span>
                                    <span>{pfMeta.label}</span>
                                  </span>
                                </td>
                                <td style={s.td}>
                                  <span style={s.typeBadge(post.postType)}>
                                    {post.postType}
                                  </span>
                                </td>
                                <td style={s.td}>
                                  <div
                                    style={{
                                      maxWidth: "300px",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      fontSize: "0.88rem",
                                    }}
                                    title={post.message}
                                  >
                                    {post.message}
                                  </div>
                                  {post.mediaUrl && (
                                    <div
                                      style={{
                                        fontSize: "0.72rem",
                                        color: C.blue,
                                        marginTop: "4px",
                                        textDecoration: "underline",
                                      }}
                                    >
                                      📎 Media Attached
                                    </div>
                                  )}
                                </td>
                                <td style={s.td}>
                                  <span
                                    style={{
                                      fontSize: "0.8rem",
                                      color: C.textSub,
                                    }}
                                  >
                                    {post.publishedDate
                                      ? new Date(
                                        post.publishedDate,
                                      ).toLocaleString()
                                      : "Date Unknown"}
                                  </span>
                                </td>
                                <td style={s.td}>
                                  <span style={s.statusBadge}>Published</span>
                                </td>
                                <td style={s.td}>
                                  <button
                                    style={s.btnView}
                                    onClick={() =>
                                      handleOpenViewModal(
                                        post.platform,
                                        post.id,
                                      )
                                    }
                                  >
                                    👁️ View
                                  </button>
                                  <button
                                    style={s.btnDelete}
                                    onClick={() => handleOpenDeleteModal(post)}
                                  >
                                    🗑️ Delete
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {/* Pagination UI */}
                      {totalPages > 1 && (
                        <div style={s.pagination}>
                          <span style={s.pagInfo}>
                            Showing {indexOfFirstPost + 1} to{" "}
                            {Math.min(indexOfLastPost, filteredPosts.length)} of{" "}
                            {filteredPosts.length} posts
                          </span>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              style={s.pagBtn(currentPage === 1)}
                              onClick={() =>
                                currentPage > 1 &&
                                setCurrentPage(currentPage - 1)
                              }
                              disabled={currentPage === 1}
                            >
                              ◀ Previous
                            </button>
                            <button
                              style={s.pagBtn(currentPage === totalPages)}
                              onClick={() =>
                                currentPage < totalPages &&
                                setCurrentPage(currentPage + 1)
                              }
                              disabled={currentPage === totalPages}
                            >
                              Next ▶
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── View Modal ── */}
      {showViewModal && (
        <div style={s.modalOverlay}>
          <div
            style={{
              background: C.white,
              borderRadius: "16px",
              width: "90%",
              maxWidth: "900px",
              boxShadow: "0 20px 40px rgba(30, 58, 95, 0.15)",
              overflow: "hidden",
              border: `1px solid ${C.border}`,
              display: "flex",
              flexDirection: "column",
              maxHeight: "90vh",
              animation: "modalSlideIn 0.3s ease-out",
            }}
          >
            {/* Sticky Header */}
            <div
              style={{
                background: C.blueSoft,
                borderBottom: `1px solid ${C.border}`,
                padding: "18px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <h3
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: 800,
                    color: C.text,
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span>🔍</span> Post Preview & Audit Log
                </h3>
                {activePost && (
                  <>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        background: `${PLATFORMS[activePost.platform]?.color || "#64748b"}15`,
                        color:
                          PLATFORMS[activePost.platform]?.color || "#64748b",
                        padding: "4px 10px",
                        borderRadius: "12px",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                      }}
                    >
                      {PLATFORMS[activePost.platform]?.icon}{" "}
                      {PLATFORMS[activePost.platform]?.label}
                    </span>
                    <span
                      style={{
                        background: C.white,
                        color: C.textSub,
                        padding: "4px 10px",
                        borderRadius: "12px",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        border: `1px solid ${C.border}`,
                        textTransform: "uppercase",
                      }}
                    >
                      {activePost.postType}
                    </span>
                  </>
                )}
              </div>
              <button
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.4rem",
                  cursor: "pointer",
                  color: C.textSub,
                  padding: "4px",
                }}
                onClick={() => setShowViewModal(false)}
              >
                ✕
              </button>
            </div>

            {/* Body Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                gap: "20px",
                padding: "24px",
                maxHeight: "calc(90vh - 140px)",
                overflowY: "auto",
                background: C.grayBg,
              }}
            >
              {fetchingDetail ? (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    padding: "60px 0",
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "2.5rem",
                      animation: "spin 1s linear infinite",
                      display: "inline-block",
                    }}
                  >
                    ⏳
                  </span>
                  <div style={{ fontWeight: 600, color: C.textSub }}>
                    Fetching full post details from platform API...
                  </div>
                </div>
              ) : activePost ? (
                <>
                  {/* Left Panel: Details & Message */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    {/* Card 1: Platform & Status */}
                    <div
                      style={{
                        background: C.white,
                        borderRadius: "12px",
                        padding: "16px",
                        border: `1px solid ${C.border}`,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                      }}
                    >
                      <h4
                        style={{
                          margin: "0 0 12px 0",
                          fontSize: "0.88rem",
                          fontWeight: 800,
                          color: C.textSub,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Platform Connection
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <span style={{ fontSize: "1.8rem" }}>
                            {PLATFORMS[activePost.platform]?.icon || "📲"}
                          </span>
                          <div>
                            <div
                              style={{
                                fontWeight: 700,
                                color: C.text,
                                fontSize: "0.95rem",
                              }}
                            >
                              {PLATFORMS[activePost.platform]?.label} Page
                            </div>
                            <div
                              style={{
                                fontSize: "0.78rem",
                                color: C.success,
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                marginTop: "2px",
                              }}
                            >
                              <span
                                style={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  background: C.success,
                                  display: "inline-block",
                                }}
                              ></span>
                              Connection Verified
                            </div>
                          </div>
                        </div>
                        <span
                          style={{
                            background: "#ecfdf5",
                            color: C.success,
                            border: "1px solid #a7f3d0",
                            padding: "4px 10px",
                            borderRadius: "20px",
                            fontSize: "0.8rem",
                            fontWeight: 700,
                          }}
                        >
                          Published
                        </span>
                      </div>
                    </div>

                    {/* Card 2: Metadata */}
                    <div
                      style={{
                        background: C.white,
                        borderRadius: "12px",
                        padding: "16px",
                        border: `1px solid ${C.border}`,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <h4
                        style={{
                          margin: "0 0 4px 0",
                          fontSize: "0.88rem",
                          fontWeight: 800,
                          color: C.textSub,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Publishing Log
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            borderBottom: `1px solid ${C.grayBg}`,
                            paddingBottom: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.85rem",
                              color: C.textSub,
                              fontWeight: 600,
                            }}
                          >
                            Post ID
                          </span>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.85rem",
                                color: C.text,
                                fontFamily: "monospace",
                                fontWeight: 600,
                              }}
                            >
                              {activePost.id
                                ? activePost.id.toString().substring(0, 18) +
                                (activePost.id.toString().length > 18
                                  ? "..."
                                  : "")
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            borderBottom: `1px solid ${C.grayBg}`,
                            paddingBottom: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.85rem",
                              color: C.textSub,
                              fontWeight: 600,
                            }}
                          >
                            Created By
                          </span>
                          <span
                            style={{
                              fontSize: "0.85rem",
                              color: C.text,
                              fontWeight: 700,
                            }}
                          >
                            Admin User
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            borderBottom: `1px solid ${C.grayBg}`,
                            paddingBottom: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.85rem",
                              color: C.textSub,
                              fontWeight: 600,
                            }}
                          >
                            Created Date
                          </span>
                          <span style={{ fontSize: "0.85rem", color: C.text }}>
                            {activePost.publishedDate
                              ? new Date(
                                activePost.publishedDate,
                              ).toLocaleString()
                              : "N/A"}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            paddingBottom: "4px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.85rem",
                              color: C.textSub,
                              fontWeight: 600,
                            }}
                          >
                            Last Updated
                          </span>
                          <span style={{ fontSize: "0.85rem", color: C.text }}>
                            {activePost.publishedDate
                              ? new Date(
                                activePost.publishedDate,
                              ).toLocaleString()
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card 3: Content */}
                    <div
                      style={{
                        background: C.white,
                        borderRadius: "12px",
                        padding: "16px",
                        border: `1px solid ${C.border}`,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                      }}
                    >
                      <h4
                        style={{
                          margin: "0 0 12px 0",
                          fontSize: "0.88rem",
                          fontWeight: 800,
                          color: C.textSub,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Post Message Content
                      </h4>
                      <div
                        style={{
                          background: C.grayBg,
                          border: `1px solid ${C.border}`,
                          borderRadius: "8px",
                          padding: "14px",
                          fontSize: "0.9rem",
                          color: C.text,
                          lineHeight: "1.6",
                          maxHeight: "160px",
                          overflowY: "auto",
                          whiteSpace: "pre-line",
                        }}
                      >
                        {activePost.message || (
                          <span
                            style={{ color: C.textMuted, fontStyle: "italic" }}
                          >
                            No caption or text content provided.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Panel: Media Preview */}
                  <div
                    style={{
                      background: C.white,
                      borderRadius: "12px",
                      padding: "16px",
                      border: `1px solid ${C.border}`,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "0.88rem",
                        fontWeight: 800,
                        color: C.textSub,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Attachment Preview
                    </h4>
                    <div
                      style={{
                        position: "relative",
                        background: C.blueSoft,
                        borderRadius: "8px",
                        height: "350px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        border: `1px solid ${C.border}`,
                      }}
                    >
                      {mediaLoading && (
                        <div
                          style={{
                            position: "absolute",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                            zIndex: 2,
                          }}
                        >
                          <span
                            style={{
                              fontSize: "2rem",
                              animation: "spin 1s linear infinite",
                              display: "inline-block",
                            }}
                          >
                            ⏳
                          </span>
                          <span
                            style={{
                              fontSize: "0.78rem",
                              color: C.textSub,
                              fontWeight: 600,
                            }}
                          >
                            Loading media...
                          </span>
                        </div>
                      )}

                      {imgError ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "10px",
                            color: C.error,
                            padding: "24px",
                            textAlign: "center",
                          }}
                        >
                          <span style={{ fontSize: "2.5rem" }}>⚠️</span>
                          <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>
                            Attachment Link Broken
                          </div>
                          <div
                            style={{
                              fontSize: "0.78rem",
                              color: C.textSub,
                              wordBreak: "break-all",
                              maxHeight: "100px",
                              overflowY: "auto",
                            }}
                          >
                            {activePost.mediaUrl}
                          </div>
                        </div>
                      ) : activePost.mediaUrl ? (
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {activePost.postType === "IMAGE" ||
                            /\.(jpg|jpeg|png|gif|webp)/i.test(
                              activePost.mediaUrl,
                            ) ? (
                            <>
                              <img
                                src={activePost.mediaUrl}
                                alt="Post Attachment"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                  cursor: "zoom-in",
                                  borderRadius: "4px",
                                }}
                                onLoad={() => setMediaLoading(false)}
                                onError={() => {
                                  setImgError(true);
                                  setMediaLoading(false);
                                }}
                                onClick={() => setIsZoomed(true)}
                              />
                              <button
                                title="Zoom Attachment"
                                style={{
                                  position: "absolute",
                                  bottom: "12px",
                                  right: "12px",
                                  background: "rgba(30, 58, 95, 0.8)",
                                  border: "none",
                                  color: C.white,
                                  borderRadius: "6px",
                                  padding: "6px 12px",
                                  fontSize: "0.78rem",
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                  transition: "all 0.15s",
                                }}
                                onClick={() => setIsZoomed(true)}
                              >
                                🔍 Zoom
                              </button>
                            </>
                          ) : (
                            <video
                              src={activePost.mediaUrl}
                              controls
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                borderRadius: "4px",
                              }}
                              onLoadStart={() => setMediaLoading(true)}
                              onLoadedData={() => setMediaLoading(false)}
                              onError={() => {
                                setImgError(true);
                                setMediaLoading(false);
                              }}
                            />
                          )}
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                            color: C.textSub,
                            padding: "24px",
                            textAlign: "center",
                          }}
                        >
                          <span style={{ fontSize: "3rem" }}>🖼️</span>
                          <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                            No Media Available
                          </div>
                          <div
                            style={{ fontSize: "0.78rem", color: C.textMuted }}
                          >
                            This message is text-only.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    padding: "40px",
                    textAlign: "center",
                    color: C.error,
                    fontWeight: 600,
                  }}
                >
                  Unable to retrieve post details.
                </div>
              )}
            </div>

            {/* Redesigned Modal Footer */}
            <div
              style={{
                borderTop: `1px solid ${C.border}`,
                padding: "16px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: C.grayBg,
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              {/* Left Side Footer Actions */}
              <div style={{ display: "flex", gap: "8px" }}>
                {activePost && (
                  <button
                    style={{
                      background: C.white,
                      color: C.text,
                      border: `1.5px solid ${C.border}`,
                      borderRadius: "8px",
                      padding: "8px 16px",
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      cursor: "pointer",
                      transition: "all 0.15s",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(activePost.id);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    {copied ? "✔ Copied!" : "📋 Copy ID"}
                  </button>
                )}

                {activePost && activePost.mediaUrl && !imgError && (
                  <button
                    style={{
                      background: C.white,
                      color: C.blue,
                      border: `1.5px solid ${C.blueLight}`,
                      borderRadius: "8px",
                      padding: "8px 16px",
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      cursor: "pointer",
                      transition: "all 0.15s",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onClick={() => {
                      const filename = `post-${activePost.id}-media`;
                      handleDownload(activePost.mediaUrl, filename);
                    }}
                  >
                    📥 Download
                  </button>
                )}
              </div>

              {/* Close Button */}
              <button
                style={{
                  background: C.blue,
                  color: C.white,
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px 20px",
                  fontSize: "0.88rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: `0 4px 12px ${C.blue}30`,
                  transition: "all 0.15s",
                }}
                onClick={() => setShowViewModal(false)}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Image Zoom Lightbox Overlay ── */}
      {isZoomed && activePost && activePost.mediaUrl && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(15, 23, 42, 0.9)",
            zIndex: 11000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
          }}
          onClick={() => setIsZoomed(false)}
        >
          <img
            src={activePost.mediaUrl}
            alt="Zoomed Attachment"
            style={{
              maxWidth: "95%",
              maxHeight: "95%",
              objectFit: "contain",
              borderRadius: "8px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
            }}
          />
          <button
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "rgba(255, 255, 255, 0.2)",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              color: "#fff",
              fontSize: "1.2rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setIsZoomed(false)}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {showDeleteModal && postToDelete && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modalCard, maxWidth: "450px" }}>
            <div style={s.modalHead}>
              <h3 style={s.modalTitle}>⚠️ Delete Published Post</h3>
              <button
                style={s.modalClose}
                onClick={() => setShowDeleteModal(false)}
              >
                ✕
              </button>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, fontSize: "0.95rem", color: C.text }}>
                Are you sure you want to delete this post from the{" "}
                <strong>{PLATFORMS[postToDelete.platform]?.label}</strong>{" "}
                published history?
              </p>
              <div
                style={{
                  background: "#f8fafc",
                  padding: "12px",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${C.error}`,
                  fontSize: "0.85rem",
                  color: C.textSub,
                }}
              >
                <strong>ID:</strong> {postToDelete.id}
                <br />
                <strong>Message:</strong>{" "}
                {postToDelete.message.substring(0, 100)}
                {postToDelete.message.length > 100 ? "..." : ""}
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.8rem",
                  color: C.error,
                  fontWeight: 600,
                }}
              >
                This will delete the configuration data record in the local CRM
                DB history list.
              </p>
            </div>
            <div style={s.modalFooter}>
              <button
                style={s.pagBtn(deleting)}
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                style={{
                  ...s.pagBtn(deleting),
                  background: C.error,
                  color: C.white,
                  border: "none",
                }}
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? "🗑️ Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Alert */}
      {toast && <div style={s.toast(toast.type)}>{toast.msg}</div>}

      {/* Animation Styles */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes modalSlideIn { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </>
  );
}
